import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Space, Descriptions, Table, Spin, Tag, Divider, Alert } from 'antd';
import { StyledCard } from '../common';
import { jsPDF } from 'jspdf';
import { ArrowLeftOutlined, ReloadOutlined, FilePdfOutlined, FileTextOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

interface LiquidacionCompra {
  id: number;
  nro_compra: number;
  comprobante: string;
  fecha: string;
  cliente: number;
  cliente_nombre: string;
  cliente_nc: string;
  cliente_cuit: string;
  cliente_direccion: string;
  cliente_localidad: string;
  cliente_provincia: string;
  subtotal: number;
  iva: number;
  impuesto_interno: number;
  retenciones: number;
  alta: number;
  asiento_contable_id: number | null;
  empresa: number;
  empresa_nombre: string;
  empresa_cuit: string;
  empresa_direccion: string;
  empresa_localidad: string;
  empresa_provincia: string;
  sucursal: number;
  sucursal_nombre: string;
}

interface TicketBalanza {
  id: number;
  numero_ticket: number;
  fecha_ticket: string | null;
  kilos_netos: number;
  numero_remito: string | null;
  dtv: string | null;
}

interface Ticket {
  id: number;
  numero_ticket: number;
  cantidad: number;
  precio: number;
  total: number;
  ticket_balanza: TicketBalanza;
}

interface RetencionInfo {
  id: number;
  descripcion: string;
  tipo: string;
  provincia: string | null;
}

interface Retencion {
  id: number;
  portonelada: number;
  porcentaje: number;
  importe: number;
  total: number;
  retencion_info: RetencionInfo;
}

interface Totales {
  total_kilos: number;
  total_subtotal: number;
  total_iva: number;
  total_retenciones: number;
  total_general: number;
  cantidad_tickets: number;
  cantidad_retenciones: number;
}

interface DataState {
  liquidacion: LiquidacionCompra;
  tickets: Ticket[];
  retenciones: Retencion[];
  totales: Totales;
}

const formatNum = (v: number) =>
  v.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (s: string) => {
  if (!s) return '';
  const [y, m, d] = s.split('-');
  return `${d}/${m}/${y}`;
};

const formatCuit = (cuit: string) => {
  if (cuit && cuit.length === 11)
    return `${cuit.slice(0, 2)}-${cuit.slice(2, 10)}-${cuit.slice(10)}`;
  return cuit || '';
};

const numeroALetras = (n: number): string => {
  const un = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
  const dec = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
  const esp: Record<number, string> = {
    10: 'DIEZ', 11: 'ONCE', 12: 'DOCE', 13: 'TRECE', 14: 'CATORCE',
    15: 'QUINCE', 16: 'DIECISEIS', 17: 'DIECISIETE', 18: 'DIECIOCHO', 19: 'DIECINUEVE',
  };
  const cen = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS',
    'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

  const proc = (x: number): string => {
    if (x === 0) return '';
    if (x < 10) return un[x];
    if (x < 20) return esp[x] || '';
    if (x < 100) {
      const d = Math.floor(x / 10), u = x % 10;
      return u === 0 ? dec[d] : `${dec[d]} Y ${un[u]}`;
    }
    if (x === 100) return 'CIEN';
    if (x < 1000) return `${cen[Math.floor(x / 100)]} ${proc(x % 100)}`.trim();
    if (x < 1000000) {
      const m = Math.floor(x / 1000);
      return `${m === 1 ? 'MIL' : `${proc(m)} MIL`} ${proc(x % 1000)}`.trim();
    }
    const m = Math.floor(x / 1000000);
    return `${m === 1 ? 'UN MILLON' : `${proc(m)} MILLONES`} ${proc(x % 1000000)}`.trim();
  };

  const abs = Math.abs(n);
  const ent = Math.floor(abs);
  const cts = Math.round((abs - ent) * 100);
  const text = `${proc(ent) || 'CERO'} CON ${String(cts).padStart(2, '0')}/100`;
  return n < 0 ? `MENOS ${text}` : text;
};

const ImprimirCompraAlgodon: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { getAccessTokenSilently, user } = useAuth0();
  const [data, setData] = useState<DataState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!id || !user?.id_empresa || !user?.id_sucursal) return;
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const res = await axios.get(
        `${process.env.REACT_APP_LIQUIDACION_URL}/obtener_liquidacion_compraalgodon/${user.id_empresa}/${user.id_sucursal}/${id}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Error al cargar la liquidación');
    } finally {
      setLoading(false);
    }
  }, [id, user?.id_empresa, user?.id_sucursal, getAccessTokenSilently]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportDetallePDF = useCallback(() => {
    if (!data) return;
    const { liquidacion, tickets, retenciones, totales } = data;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const W = doc.internal.pageSize.width;
    const lm = 10, rm = W - 10;
    let y = 12;

    // Watermark para anuladas (se dibuja primero, queda detrás del contenido)
    if (liquidacion.alta === 0) {
      const H = doc.internal.pageSize.height;
      doc.setFontSize(72); doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 190, 190);
      doc.text('ANULADA', W / 2, H / 2, { align: 'center', angle: 38 });
      doc.setFontSize(9); doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
    }

    // "USO INTERNO" — esquina superior derecha
    doc.setFontSize(6); doc.setFont('helvetica', 'normal');
    doc.setTextColor(156, 163, 175);
    doc.text('USO INTERNO', rm, 7, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    // Header 3 columnas: empresa | título | N°/fecha
    doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.text(liquidacion.empresa_nombre, lm, y);
    doc.setFont('helvetica', 'normal');
    if (liquidacion.empresa_cuit)
      doc.text(`CUIT: ${formatCuit(liquidacion.empresa_cuit)}`, lm, y + 4);
    const addr = [liquidacion.empresa_direccion, liquidacion.empresa_localidad, liquidacion.empresa_provincia]
      .filter(Boolean).join(', ');
    if (addr) doc.text(addr, lm, y + 8);

    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    doc.text('LIQUIDACIÓN COMPRA ALGODÓN', W / 2, y + 2, { align: 'center' });
    doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    doc.setTextColor(107, 114, 128);
    doc.text('INFORME DE DETALLE', W / 2, y + 7, { align: 'center' });
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(12); doc.setFont('helvetica', 'bold');
    doc.text(`N° ${String(liquidacion.nro_compra).padStart(8, '0')}`, rm, y, { align: 'right' });
    doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    doc.text(`Fecha: ${formatDate(liquidacion.fecha)}`, rm, y + 5, { align: 'right' });

    y += 14;
    doc.setFontSize(9);
    doc.text('Cliente: ', lm, y);
    doc.setFont('helvetica', 'bold');
    doc.text(`${liquidacion.cliente_nombre}  (NC: ${liquidacion.cliente_nc})`, lm + 18, y);
    doc.setFont('helvetica', 'normal');
    if (liquidacion.comprobante)
      doc.text(`Comprobante: ${liquidacion.comprobante}`, rm, y, { align: 'right' });

    y += 3; doc.setLineWidth(0.6); doc.line(lm, y, rm, y); doc.setLineWidth(0.2); y += 5;

    // Tabla tickets
    const cN = lm, cF = 32, cR = 58, cD = 110, cK = 167, cP = 212, cS = rm;
    doc.setFillColor(243, 244, 246);
    doc.rect(lm, y - 3, rm - lm, 5.5, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
    doc.text('N° Ticket', cN, y);
    doc.text('Fecha', cF, y);
    doc.text('N° Remito', cR, y);
    doc.text('DTV', cD, y);
    doc.text('Kg Netos', cK, y, { align: 'right' });
    doc.text('Precio/Ton', cP, y, { align: 'right' });
    doc.text('Subtotal $', cS, y, { align: 'right' });
    y += 2; doc.line(lm, y, rm, y); y += 4;

    doc.setFont('helvetica', 'normal');
    let rowIdx = 0;
    for (const t of tickets) {
      if (rowIdx % 2 === 1) { doc.setFillColor(250, 250, 250); doc.rect(lm, y - 3, rm - lm, 5, 'F'); }
      const bal = t.ticket_balanza;
      doc.text(String(t.numero_ticket), cN, y);
      doc.text(bal.fecha_ticket ? formatDate(bal.fecha_ticket) : '-', cF, y);
      doc.text(bal.numero_remito ? String(bal.numero_remito).substring(0, 22) : '-', cR, y);
      doc.text(bal.dtv ? String(bal.dtv).substring(0, 18) : '-', cD, y);
      doc.text(formatNum(t.cantidad), cK, y, { align: 'right' });
      doc.text(formatNum(t.precio), cP, y, { align: 'right' });
      doc.text(formatNum(t.total), cS, y, { align: 'right' });
      y += 5; rowIdx++;
      if (y > 165) { doc.addPage(); y = 15; }
    }

    doc.setFillColor(240, 242, 245);
    doc.rect(lm, y - 3, rm - lm, 5.5, 'F');
    doc.setLineWidth(0.5); doc.line(lm, y - 3, rm, y - 3); doc.setLineWidth(0.2);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL', cN, y);
    doc.text(formatNum(totales.total_kilos), cK, y, { align: 'right' });
    doc.text(formatNum(totales.total_subtotal), cS, y, { align: 'right' });
    y += 8;

    // Retenciones aplicadas — tabla completa
    if (retenciones.length > 0) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5);
      doc.text('RETENCIONES APLICADAS:', lm, y); y += 5;
      const rD = lm, rPT = 120, rPC = 150, rI = lm + 155;
      doc.setFontSize(8);
      doc.setFillColor(243, 244, 246);
      doc.rect(lm, y - 3, rI - lm + 5, 5.5, 'F');
      doc.text('Descripción', rD, y);
      doc.text('Por Ton', rPT, y, { align: 'right' });
      doc.text('%', rPC, y, { align: 'right' });
      doc.text('Importe $', rI, y, { align: 'right' });
      y += 2; doc.line(lm, y, rI + 5, y); y += 4;
      doc.setFont('helvetica', 'normal');
      let rIdx = 0;
      for (const r of retenciones) {
        if (rIdx % 2 === 1) { doc.setFillColor(250, 250, 250); doc.rect(lm, y - 3, rI - lm + 5, 5, 'F'); }
        doc.text(r.retencion_info.descripcion.substring(0, 65), rD, y);
        doc.text(formatNum(r.portonelada), rPT, y, { align: 'right' });
        doc.text(formatNum(r.porcentaje), rPC, y, { align: 'right' });
        doc.text(formatNum(r.total), rI, y, { align: 'right' });
        y += 5; rIdx++;
      }
      doc.setFillColor(240, 242, 245);
      doc.rect(lm, y - 3, rI - lm + 5, 5.5, 'F');
      doc.line(lm, y - 3, rI + 5, y - 3);
      doc.setFont('helvetica', 'bold');
      doc.text('Total Retenciones:', rPC, y, { align: 'right' });
      doc.text(formatNum(totales.total_retenciones), rI, y, { align: 'right' });
      y += 8;
    }

    // Totales
    const tx = rm - 82;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
    doc.text('Subtotal:', tx, y);
    doc.text(`$ ${formatNum(totales.total_subtotal)}`, rm, y, { align: 'right' }); y += 5;
    doc.text('IVA 10,5%:', tx, y);
    doc.text(`$ ${formatNum(totales.total_iva)}`, rm, y, { align: 'right' }); y += 5;
    if (totales.total_retenciones > 0) {
      doc.text('Retenciones:', tx, y);
      doc.text(`($ ${formatNum(totales.total_retenciones)})`, rm, y, { align: 'right' }); y += 5;
    }
    doc.setLineWidth(0.5); doc.line(tx, y, rm, y); doc.setLineWidth(0.2); y += 3;
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL A PAGAR:', tx, y);
    doc.text(`$ ${formatNum(totales.total_general)}`, rm, y, { align: 'right' }); y += 8;

    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
    doc.setTextColor(55, 65, 81);
    doc.text(doc.splitTextToSize(`Son Pesos: ${numeroALetras(totales.total_general)}`, rm - lm - 85), lm, y);
    doc.setTextColor(0, 0, 0);

    doc.save(`CompraAlgodon-Detalle-${liquidacion.nro_compra}.pdf`);
  }, [data]);

  const exportFacturaPDF = useCallback(() => {
    if (!data) return;
    const { liquidacion, tickets, retenciones, totales } = data;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = doc.internal.pageSize.width;
    const lm = 10, rm = W - 10;
    let y = 10;

    // Cabecera: dos cajas (empresa | liquidación/N°)
    const boxW = W - 20;
    const leftW = boxW * 0.62;
    const rightW = boxW * 0.38;
    const boxH = 46;
    doc.setLineWidth(0.5);
    doc.rect(lm, y, leftW, boxH);
    doc.rect(lm + leftW, y, rightW, boxH);
    doc.setLineWidth(0.2);

    let cy = y + 8;
    doc.setFontSize(11); doc.setFont('helvetica', 'bold');
    doc.text(liquidacion.empresa_nombre, lm + 4, cy); cy += 6;
    doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    if (liquidacion.empresa_cuit) { doc.text(`CUIT: ${formatCuit(liquidacion.empresa_cuit)}`, lm + 4, cy); cy += 5; }
    if (liquidacion.empresa_direccion) { doc.text(`Dir: ${liquidacion.empresa_direccion}`, lm + 4, cy); cy += 5; }
    const loc = [liquidacion.empresa_localidad, liquidacion.empresa_provincia].filter(Boolean).join(', ');
    if (loc) doc.text(loc, lm + 4, cy);

    const rx = lm + leftW + 5;
    let iy = y + 8;
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    doc.text('LIQUIDACIÓN', rx, iy); iy += 6;
    doc.setFontSize(8.5); doc.setFont('helvetica', 'normal');
    doc.setTextColor(55, 65, 81);
    doc.text('COMPRA ALGODÓN', rx, iy); iy += 6;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12); doc.setFont('helvetica', 'bold');
    doc.text(`N° ${String(liquidacion.nro_compra).padStart(8, '0')}`, rx, iy); iy += 7;
    doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    doc.text(`Fecha: ${formatDate(liquidacion.fecha)}`, rx, iy); iy += 5;
    if (liquidacion.comprobante) doc.text(`Cpte: ${liquidacion.comprobante}`, rx, iy);

    y += boxH + 5;

    // Destinatario
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold'); doc.text('Señores:', lm, y);
    doc.setFont('helvetica', 'normal'); doc.text(liquidacion.cliente_nombre, lm + 24, y); y += 5;
    if (liquidacion.cliente_cuit) {
      doc.setFont('helvetica', 'bold'); doc.text('CUIT:', lm, y);
      doc.setFont('helvetica', 'normal'); doc.text(formatCuit(liquidacion.cliente_cuit), lm + 14, y); y += 5;
    }
    const clientAddr = [liquidacion.cliente_direccion, liquidacion.cliente_localidad, liquidacion.cliente_provincia]
      .filter(Boolean).join(', ');
    if (clientAddr) {
      doc.setFont('helvetica', 'bold'); doc.text('Dom:', lm, y);
      doc.setFont('helvetica', 'normal');
      doc.text(doc.splitTextToSize(clientAddr, rm - lm - 14), lm + 14, y); y += 5;
    }
    y += 2;

    // Línea introductoria en itálica
    doc.setFontSize(7.5); doc.setFont('helvetica', 'italic');
    doc.setTextColor(55, 65, 81);
    doc.text('Por la presente liquidamos a Ud. la compra del algodón entregado en esta planta, conforme al siguiente detalle:', lm, y);
    doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'normal');
    y += 5; doc.line(lm, y, rm, y); y += 5;

    // Tabla tickets (sin Remito/DTV)
    const hN = lm, hF = 35, hK = 108, hP = 148, hS = rm;
    doc.setFontSize(8);
    doc.setFillColor(243, 244, 246);
    doc.rect(lm, y - 3, rm - lm, 5.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('N° Ticket', hN, y);
    doc.text('Fecha', hF, y);
    doc.text('Kg Netos', hK, y, { align: 'right' });
    doc.text('Precio/Ton', hP, y, { align: 'right' });
    doc.text('Subtotal $', hS, y, { align: 'right' });
    y += 2; doc.line(lm, y, rm, y); y += 4;

    doc.setFont('helvetica', 'normal');
    let rIdx = 0;
    for (const t of tickets) {
      if (rIdx % 2 === 1) { doc.setFillColor(250, 250, 250); doc.rect(lm, y - 3, rm - lm, 5, 'F'); }
      const bal = t.ticket_balanza;
      doc.text(String(t.numero_ticket), hN, y);
      doc.text(bal.fecha_ticket ? formatDate(bal.fecha_ticket) : '-', hF, y);
      doc.text(formatNum(t.cantidad), hK, y, { align: 'right' });
      doc.text(formatNum(t.precio), hP, y, { align: 'right' });
      doc.text(formatNum(t.total), hS, y, { align: 'right' });
      y += 5; rIdx++;
      if (y > 245) { doc.addPage(); y = 15; }
    }

    doc.setFillColor(240, 242, 245);
    doc.rect(lm, y - 3, rm - lm, 5.5, 'F');
    doc.setLineWidth(0.5); doc.line(lm, y - 3, rm, y - 3); doc.setLineWidth(0.2);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total: ${formatNum(totales.total_kilos)} kg`, hN, y);
    doc.text(formatNum(totales.total_subtotal), hS, y, { align: 'right' });
    y += 8;

    // Totales con retenciones como descuentos
    const tx = rm - 92;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
    doc.text('Subtotal:', tx, y); doc.text(`$ ${formatNum(totales.total_subtotal)}`, rm, y, { align: 'right' }); y += 5;
    doc.text('IVA 10,5%:', tx, y); doc.text(`$ ${formatNum(totales.total_iva)}`, rm, y, { align: 'right' }); y += 5;

    if (totales.total_retenciones > 0) {
      doc.setLineWidth(0.3); doc.line(tx, y, rm, y); doc.setLineWidth(0.2); y += 3;
      doc.setFontSize(8); doc.setTextColor(107, 114, 128);
      doc.text('Bruto a cobrar:', tx, y);
      doc.text(`$ ${formatNum(totales.total_subtotal + totales.total_iva)}`, rm, y, { align: 'right' }); y += 5;
      doc.setTextColor(185, 28, 28); doc.setFontSize(8.5);
      const descW = rm - tx - 32;
      for (const r of retenciones) {
        const label = `Ret. ${r.retencion_info.descripcion} (${formatNum(r.porcentaje)}%):`;
        const lines = doc.splitTextToSize(label, descW);
        doc.text(lines, tx, y);
        doc.text(`($ ${formatNum(r.total)})`, rm, y, { align: 'right' });
        y += lines.length * 4.5 + 0.5;
        if (y > 265) { doc.addPage(); y = 15; }
      }
      doc.setTextColor(0, 0, 0);
    }

    doc.setLineWidth(0.5); doc.line(tx, y, rm, y); doc.setLineWidth(0.2); y += 3;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
    doc.text('NETO A COBRAR:', tx, y);
    doc.text(`$ ${formatNum(totales.total_general)}`, rm, y, { align: 'right' }); y += 8;

    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
    doc.setTextColor(55, 65, 81);
    doc.text(doc.splitTextToSize(`Son Pesos: ${numeroALetras(totales.total_general)}`, rm - lm), lm, y);
    doc.setTextColor(0, 0, 0);
    y += 8;

    // Bloque firma
    doc.setLineWidth(0.3); doc.line(lm, y + 18, rm, y + 18); doc.setLineWidth(0.2); y += 2;
    doc.setFontSize(7.5);
    doc.setTextColor(107, 114, 128);
    const midX = lm + (rm - lm) / 2;
    doc.text('Recibí conforme en concepto de pago total:', lm, y + 20);
    doc.text('DNI / CUIT:', midX + 5, y + 20);
    doc.setLineWidth(0.5);
    doc.line(lm, y + 32, midX - 5, y + 32);
    doc.line(midX + 5, y + 32, rm, y + 32);
    doc.setLineWidth(0.2);
    doc.setFontSize(7);
    doc.text('Firma y aclaración', lm, y + 35);
    doc.text('Lugar y fecha', midX + 5, y + 35);
    doc.setTextColor(0, 0, 0);

    doc.save(`CompraAlgodon-Factura-${liquidacion.nro_compra}.pdf`);
  }, [data]);

  const ticketColumns = [
    { title: 'N° Ticket', dataIndex: 'numero_ticket', key: 'nro', width: 90, align: 'center' as const },
    {
      title: 'Fecha', dataIndex: ['ticket_balanza', 'fecha_ticket'], key: 'fecha', width: 100,
      render: (v: string) => v ? formatDate(v) : '-',
    },
    { title: 'N° Remito', dataIndex: ['ticket_balanza', 'numero_remito'], key: 'remito', ellipsis: true },
    { title: 'DTV', dataIndex: ['ticket_balanza', 'dtv'], key: 'dtv', ellipsis: true },
    {
      title: 'Kg Netos', dataIndex: 'cantidad', key: 'kg', width: 120, align: 'right' as const,
      render: (v: number) => formatNum(v),
    },
    {
      title: 'Precio/Ton', dataIndex: 'precio', key: 'precio', width: 120, align: 'right' as const,
      render: (v: number) => formatNum(v),
    },
    {
      title: 'Subtotal', dataIndex: 'total', key: 'total', width: 120, align: 'right' as const,
      render: (v: number) => `$ ${formatNum(v)}`,
    },
  ];

  const retencionColumns = [
    { title: 'Descripción', dataIndex: ['retencion_info', 'descripcion'], key: 'desc', ellipsis: true },
    { title: 'Tipo', dataIndex: ['retencion_info', 'tipo'], key: 'tipo', width: 80 },
    {
      title: 'Por Ton', dataIndex: 'portonelada', key: 'pt', width: 100, align: 'right' as const,
      render: (v: number) => formatNum(v),
    },
    {
      title: '%', dataIndex: 'porcentaje', key: 'pct', width: 80, align: 'right' as const,
      render: (v: number) => formatNum(v),
    },
    {
      title: 'Importe', dataIndex: 'total', key: 'imp', width: 120, align: 'right' as const,
      render: (v: number) => `$ ${formatNum(v)}`,
    },
  ];

  if (loading) return (
    <div style={{ textAlign: 'center', marginTop: 80 }}>
      <Spin size="large" />
      <p style={{ marginTop: 20 }}>Cargando liquidación...</p>
    </div>
  );

  if (error) return (
    <div style={{ padding: 20 }}>
      <StyledCard darkHeader={false}>
        <div style={{ textAlign: 'center' }}>
          <h3 style={{ color: '#ff4d4f' }}>Error</h3>
          <p>{error}</p>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/listar_compras_algodon')}>Volver</Button>
            <Button type="primary" icon={<ReloadOutlined />} onClick={fetchData}>Reintentar</Button>
          </Space>
        </div>
      </StyledCard>
    </div>
  );

  if (!data) return null;

  const { liquidacion, tickets, retenciones, totales } = data;

  return (
    <div style={{ padding: 20 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {liquidacion.alta === 0 && (
          <Alert
            type="error"
            showIcon
            message="Liquidación anulada"
            description="Esta liquidación fue anulada. El detalle puede imprimirse con marca de agua ANULADA. La factura al cliente no está disponible."
            style={{ marginBottom: 16 }}
          />
        )}
        {liquidacion.alta !== 0 && !liquidacion.comprobante && (
          <Alert
            type="warning"
            showIcon
            message="Factura al cliente no disponible"
            description="La liquidación debe tener un número de comprobante asignado para poder emitir la factura al cliente. El detalle interno sí puede imprimirse."
            style={{ marginBottom: 16 }}
          />
        )}
        <StyledCard
          title={`Liquidación Compra Algodón N° ${liquidacion.nro_compra}`}
          extra={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/listar_compras_algodon')}>
                Volver
              </Button>
              <Button
                type="primary"
                icon={<FilePdfOutlined />}
                onClick={exportDetallePDF}
              >
                Imprimir Detalle
              </Button>
              <Button
                icon={<FileTextOutlined />}
                onClick={exportFacturaPDF}
                disabled={liquidacion.alta === 0 || !liquidacion.comprobante}
                ghost={liquidacion.alta === 0 || !liquidacion.comprobante}
                title={
                  liquidacion.alta === 0
                    ? 'No disponible: liquidación anulada'
                    : !liquidacion.comprobante
                    ? 'Disponible solo cuando la liquidación tenga un comprobante asignado'
                    : undefined
                }
                style={liquidacion.alta !== 0 && !!liquidacion.comprobante ? { backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' } : undefined}
              >
                Imprimir Factura
              </Button>
            </Space>
          }
        >
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Empresa" span={2}>{liquidacion.empresa_nombre}</Descriptions.Item>
            {liquidacion.empresa_cuit && (
              <Descriptions.Item label="CUIT">{formatCuit(liquidacion.empresa_cuit)}</Descriptions.Item>
            )}
            {liquidacion.empresa_direccion && (
              <Descriptions.Item label="Dirección">
                {[liquidacion.empresa_direccion, liquidacion.empresa_localidad, liquidacion.empresa_provincia]
                  .filter(Boolean).join(', ')}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Fecha">{formatDate(liquidacion.fecha)}</Descriptions.Item>
            <Descriptions.Item label="N° Compra">{liquidacion.nro_compra}</Descriptions.Item>
            <Descriptions.Item label="Cliente">{liquidacion.cliente_nombre}</Descriptions.Item>
            <Descriptions.Item label="NC">{liquidacion.cliente_nc}</Descriptions.Item>
            {liquidacion.comprobante && (
              <Descriptions.Item label="Comprobante">{liquidacion.comprobante}</Descriptions.Item>
            )}
            <Descriptions.Item label="Estado">
              {liquidacion.alta === 0
                ? <Tag color="red">Anulada</Tag>
                : liquidacion.alta === 2
                  ? <Tag color="blue">Emitida</Tag>
                  : <Tag color="green">Activa</Tag>}
            </Descriptions.Item>
          </Descriptions>

          <Divider orientation="left">Tickets</Divider>
          <Table
            columns={ticketColumns}
            dataSource={tickets}
            rowKey="id"
            pagination={false}
            size="small"
            bordered
            scroll={{ x: 900 }}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}><strong>Total</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <strong>{formatNum(totales.total_kilos)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} />
                <Table.Summary.Cell index={6} align="right">
                  <strong>$ {formatNum(totales.total_subtotal)}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />

          {retenciones.length > 0 && (
            <>
              <Divider orientation="left">Retenciones</Divider>
              <Table
                columns={retencionColumns}
                dataSource={retenciones}
                rowKey="id"
                pagination={false}
                size="small"
                bordered
              />
            </>
          )}

          <Divider />
          <Descriptions
            bordered
            column={1}
            size="small"
            style={{ maxWidth: 420, marginLeft: 'auto' }}
          >
            <Descriptions.Item label="Subtotal">$ {formatNum(totales.total_subtotal)}</Descriptions.Item>
            <Descriptions.Item label="IVA">$ {formatNum(totales.total_iva)}</Descriptions.Item>
            {totales.total_retenciones > 0 && (
              <Descriptions.Item label="Retenciones">
                ($ {formatNum(totales.total_retenciones)})
              </Descriptions.Item>
            )}
            <Descriptions.Item label={<strong>TOTAL</strong>}>
              <strong>$ {formatNum(totales.total_general)}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Son Pesos">
              {numeroALetras(totales.total_general)}
            </Descriptions.Item>
          </Descriptions>
        </StyledCard>
      </Space>
    </div>
  );
};

export default ImprimirCompraAlgodon;
